import React, { useEffect, useRef, useState } from 'react';
import { visit } from 'navigation/clientNavigation';
import { __ } from 'i18n/index';

// services\chat\mute\RoomMuteDuration
const MUTE_CHOICES = [
	{ duration: 'hour', label: () => __('1 hour') },
	{ duration: 'eight_hours', label: () => __('8 hours') },
	{ duration: 'indefinite', label: () => __('Until you unmute') },
];

// After Copy link the row reads "Link copied" this long, then the menu closes.
export const COPIED_CLOSE_MILLISECONDS = 900;

// The room menu, opened from a room in the Rooms list (right-click, its ⋯, or a long
// press). index/total/onMove come from the list it was opened in; without them there
// is no Move row. onOpenRoom shows the room in Chat, for Rules and Pinned message.
export default function RoomMenu({ room, index, total, onMove, onOpenRoom, onShowRules, closePopup }) {
	const [choosingMute, setChoosingMute] = useState(false);
	const [confirmingLeave, setConfirmingLeave] = useState(false);
	const [linkCopied, setLinkCopied] = useState(false);
	const closeTimer = useRef(null);
	useEffect(() => () => clearTimeout(closeTimer.current), []);
	// Up and down keep the menu open, so it follows the room's place in the list.
	const [position, setPosition] = useState(index);
	const close = () => closePopup && closePopup();
	const hasUnread = room.chatMessages.hasUnreadMessages() || !!room.chatMessages.hasNewMentions;
	const canMove = typeof position === 'number' && typeof total === 'number' && !!onMove;

	const moveTo = (toIndex) => {
		onMove(toIndex);
		setPosition(toIndex);
	};

	const copyLink = () => copyToClipboard(room.getUrl(), () => {
		setLinkCopied(true);
		closeTimer.current = setTimeout(close, COPIED_CLOSE_MILLISECONDS);
	});

	const run = (action) => () => {
		action();
		close();
	};

	if(confirmingLeave) {
		return (
			<div className='specific_menu active context_menu created context_menu--message context_menu--room'>
				<RoomMenuHead room={room} />
				<div className='room_menu__confirm'>
					<p className='room_menu__confirm-question'>{__('Leave #%s?', room.name)}</p>
					<p className='room_menu__confirm-hint'>{__('You can rejoin it from Browse Rooms.')}</p>
					<div className='room_menu__confirm-buttons'>
						<button type='button' className='room_menu__button' onClick={() => setConfirmingLeave(false)}>{__('Cancel')}</button>
						<button type='button' className='room_menu__button room_menu__button--danger' onClick={run(() => room.exit())}>{__('Leave')}</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className='specific_menu active context_menu created context_menu--message context_menu--room'>
			<RoomMenuHead room={room} />
			<div className='message_menu__rows'>
				{hasUnread &&
				<RoomMenuRow icon='check' onClick={run(() => room.markRead())}>{__('Mark as read')}</RoomMenuRow>
				}
				{room.isMuted() ? (
					<RoomMenuRow icon='bell' onClick={run(() => room.unmute())} hint={muteHint(room)}>{__('Unmute')}</RoomMenuRow>
				) : (
					<React.Fragment>
						<RoomMenuRow icon='bell-slash' onClick={() => setChoosingMute((choosing) => !choosing)} aria-expanded={choosingMute}>
							{__('Mute')}
						</RoomMenuRow>
						{choosingMute &&
						<div className='room_menu__choices'>
							{MUTE_CHOICES.map(({ duration, label }) => (
								<button type='button' key={duration} className='room_menu__choice' onClick={run(() => room.muteFor(duration))}>{label()}</button>
							))}
						</div>
						}
					</React.Fragment>
				)}
				{linkCopied
					? <RoomMenuRow icon='check' className='message_menu__row is-flashed'>{__('Link copied')}</RoomMenuRow>
					: <RoomMenuRow icon='link' onClick={copyLink}>{__('Copy link')}</RoomMenuRow>}
				{!!room.has_ladder &&
				<RoomMenuRow icon='book' onClick={run(() => { onOpenRoom(room); onShowRules(); })}>{__('Rules')}</RoomMenuRow>
				}
				{room.hasDescription() &&
				<RoomMenuRow icon='thumb-tack' onClick={run(() => { onOpenRoom(room); room.descriptionReset(); })}>{__('Pinned message')}</RoomMenuRow>
				}
			</div>
			{(canMove || room.hasMod(myUser)) &&
			<div className='message_menu__rows'>
				{canMove &&
				<div className='message_menu__row room_menu__move'>
					<i className='fa fa-bars' aria-hidden='true' />
					<span>{__('Move')}</span>
					<span className='room_menu__move-buttons'>
						<MoveButton icon='angle-double-up' label={__('Move to top')} disabled={position === 0} onClick={run(() => onMove(0))} />
						<MoveButton icon='arrow-up' label={__('Move up')} disabled={position === 0} onClick={() => moveTo(position - 1)} />
						<MoveButton icon='arrow-down' label={__('Move down')} disabled={position >= total - 1} onClick={() => moveTo(position + 1)} />
					</span>
				</div>
				}
				{room.hasMod(myUser) &&
				<RoomMenuRow icon='cog' onClick={run(() => visit(room.getEditUrl()))}>{__('Room settings')}</RoomMenuRow>
				}
			</div>
			}
			<div className='message_menu__rows'>
				<RoomMenuRow icon='sign-out' danger onClick={() => setConfirmingLeave(true)}>{__('Leave room')}</RoomMenuRow>
			</div>
		</div>
	);
}

function muteHint(room) {
	const until = room.mutedUntil();
	if(until === null) {
		return __('Until you unmute');
	}
	return __('Until %s', new Date(until * 1000).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }));
}

function RoomMenuHead({ room }) {
	return (
		<div className='room_menu__head'>
			{room.ladder
				? <img className='room_menu__icon' src={room.ladder.miniImageUrl()} alt='' />
				: <span className='room_menu__hash' aria-hidden='true'>#</span>}
			<span className='room_menu__name'>{room.name}</span>
		</div>
	);
}

function RoomMenuRow({ icon, danger = false, hint, className = 'message_menu__row', children, ...props }) {
	return (
		<button type='button' className={`${className}${danger ? ' message_menu__row--danger' : ''}`} {...props}>
			<i className={`fa fa-${icon}`} aria-hidden='true' />
			<span>{children}</span>
			{hint && <span className='room_menu__row-hint'>{hint}</span>}
		</button>
	);
}

function MoveButton({ icon, label, disabled, onClick }) {
	return (
		<button type='button' className='room_menu__move-button' aria-label={label} title={label} disabled={disabled} onClick={onClick}>
			<i className={`fa fa-${icon}`} aria-hidden='true' />
		</button>
	);
}
